import { useTranslation } from '../i18n';

import React, { useState, useEffect } from 'react';
import { Select, Spin, Input, Button, Space } from 'antd';
import geoService from '../services/geoService';
import { CountryLabel } from './CountryFlag';

const { Option } = Select;

const IspSelector = ({ value = {}, onChange, geoCountry }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [ispCountries, setIspCountries] = useState([]);
    const [ispTypes, setIspTypes] = useState([]);
    const [isps, setIsps] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedIsp, setSelectedIsp] = useState(null);
    const [searchKeyword, setSearchKeyword] = useState('');

    // 是否锁定国家选择（当geoCountry有值且不是'0'时锁定）
    const isCountryLocked = geoCountry && geoCountry !== '0' && geoCountry !== '';

    // 加载ISP国家列表
    useEffect(() => {
        const fetchIspCountries = async () => {
            setLoading(true);
            try {
                const result = await geoService.getIspCountries();
                if (result.success) {
                    setIspCountries(result.data);
                }
            } catch (error) {
                console.error('Error loading ISP countries:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchIspCountries();
    }, []);

    // 当国家变化时加载ISP类型
    useEffect(() => {
        if (!selectedCountry) return;

        const fetchIspTypes = async () => {
            setLoading(true);
            try {
                const result = await geoService.getIspTypes(selectedCountry);
                if (result.success) {
                    setIspTypes(result.data);
                }
            } catch (error) {
                console.error('Error loading ISP types:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchIspTypes();
    }, [selectedCountry]);

    // 当国家和类型变化时加载ISP列表
    useEffect(() => {
        if (!selectedCountry) return;

        const fetchIsps = async () => {
            setLoading(true);
            try {
                const result = await geoService.getIsps(selectedCountry, selectedType, searchKeyword);
                if (result.success) {
                    setIsps(result.data);
                }
                // console.log("selectedIsp", selectedIsp)
            } catch (error) {
                console.error('Error loading ISPs:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchIsps();
    }, [selectedCountry, selectedType, searchKeyword]);

    // 从外部值初始化组件状态
    useEffect(() => {
        if (value) {
            setSelectedCountry(value.isp_country || null);
            setSelectedType(value.isp_type || null);
            // isp 为 0 或 '0' 表示未选择，应显示为 null
            const ispValue = value.isp;
            setSelectedIsp(ispValue && ispValue !== '0' && ispValue !== 0 ? parseInt(ispValue) : null);
        }
    }, [value]);

    // 当地理位置国家变化时，自动同步ISP国家
    useEffect(() => {
        if (isCountryLocked && geoCountry !== selectedCountry) {
            setSelectedCountry(geoCountry);
            setSelectedType(null);
            setSelectedIsp(null);

            // 更新外部表单值
            if (onChange) {
                onChange({
                    isp_country: geoCountry,
                    isp_type: null,
                    isp: null
                });
            }
        }
    }, [geoCountry, isCountryLocked]);

    const handleCountryChange = (country) => {
        setSelectedCountry(country);
        setSelectedType(null);
        setSelectedIsp(null);

        // 更新外部表单值
        if (onChange) {
            onChange({
                isp_country: country,
                isp_type: null,
                isp: null
            });
        }
    };

    const handleTypeChange = (type) => {
        setSelectedType(type);
        setSelectedIsp(null);

        // 更新外部表单值
        if (onChange) {
            onChange({
                isp_country: selectedCountry,
                isp_type: type,
                isp: null
            });
        }
    };

    const handleIspChange = (isp) => {
        setSelectedIsp(isp);

        // 查找ISP名称
        const selectedIspObj = isps.find(item => item.id === isp);
        const ispName = selectedIspObj ? selectedIspObj.name : '';

        // 更新外部表单值
        if (onChange) {
            onChange({
                isp_country: selectedCountry,
                isp_type: selectedType,
                isp: isp,
                isp_name: ispName
            });
        }
    };

    const handleSearch = () => {
        // 搜索关键词已通过useState和useEffect触发ISP列表刷新
    };

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" style={{ width: '100%' }}>
                {/* ISP国家选择 */}
                <Select
                    placeholder={t('Select an ISP country')}
                    style={{ width: '100%' }}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    allowClear={!isCountryLocked}
                    disabled={isCountryLocked}
                >
                    {ispCountries.map(country => (
                        <Option key={country.country} value={country.country}>
                            <CountryLabel code={country.country}>{country.country_name}</CountryLabel>
                        </Option>
                    ))}
                </Select>

                {/* ISP类型选择 */}
                {selectedCountry && (
                    <Select
                        placeholder={t('Select an ISP type')}
                        style={{ width: '100%' }}
                        value={selectedType}
                        onChange={handleTypeChange}
                        allowClear
                    >
                        {ispTypes.map(type => (
                            <Option key={type.code || type} value={type.code || type}>{type.name || type}</Option>
                        ))}
                    </Select>
                )}

                {/* ISP搜索 */}
                {selectedCountry && (
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            placeholder={t('Search ISP')}
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                            onPressEnter={handleSearch}
                        />
                        <Button type="primary" onClick={handleSearch}>{t('Search')}</Button>
                    </Space.Compact>
                )}

                {/* ISP选择 */}
                {selectedCountry && (
                    <Select
                        placeholder={t('Select an ISP')}
                        style={{ width: '100%' }}
                        value={selectedIsp}
                        onChange={handleIspChange}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
{/*
                        <Option value="0">无</Option>
*/}
                        {(isps || []).filter(isp => isp.id != null).map(isp => (
                            <Option key={isp.id} value={isp.id}>{isp.name}</Option>
                        ))}
                    </Select>
                )}
            </Space>
        </Spin>
    );
};

export default IspSelector;
